import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from "../design/dialog";
import {
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from "../design/fieldset";
import { Input } from "../design/input";
import { Button } from "../design/button";
import { useHolidaySchedule } from "../contexts/HolidayScheduleContext";
import { HolidaySchedule } from "../models/HolidaySchedule";
import DragAndDropFile from "../DragAndDropFile";

interface Context {
  openDialog: (onCreated?: (filingId: HolidaySchedule["_id"]) => void) => void;
  closeDialog: () => void;
  isOpen: boolean;
}

interface Errors {
  file?: string;
  name?: string;
  side?: string;
}

const CreateHolidayScheduleDialogProviderContext = createContext<
  Context | undefined
>(undefined);

export const CreateHolidayScheduleDialogProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { createHolidaySchedule } = useHolidaySchedule();
  const [isOpen, setIsOpen] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [step, setStep] = useState<"file" | "form">("file");

  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState<string>("");
  const [orderDate, setOrderDate] = useState<string>(
    new Date().toISOString().split("T")[0],
  );
  const [isUploading, setIsUploading] = useState(false);
  const [onCreatedCallback, setOnCreatedCallback] = useState<
    ((filingId: HolidaySchedule["_id"]) => void) | undefined
  >(undefined);

  const openDialog = (
    onCreated?: (filingId: HolidaySchedule["_id"]) => void,
  ) => {
    setOnCreatedCallback(() => onCreated);
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
  };

  const validateForm = () => {
    const newErrors: Errors = {};
    if (!name.trim()) newErrors.name = "A name is required.";
    if (!file) newErrors.file = "A file is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (file) {
      setName(file.name);
    }
  }, [file]);

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setErrors({});
    const droppedFile = e.dataTransfer.files?.[0];
    if (!droppedFile) return;
    if (
      ![
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ].includes(droppedFile.type)
    )
      return setErrors({
        file: "Invalid file type. Only PDF and Word files are allowed.",
      });
    if (droppedFile.size > 10 * 1024 * 1024)
      // 10 MB limit
      return setErrors({ file: "File size exceeds 10 MB." });
    setFile(droppedFile);
    setStep("form");
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsUploading(true);

    try {
      const filingId = await createHolidaySchedule(file!, {
        name,
        orderDate: orderDate ? new Date(orderDate) : undefined,
        status: "draft",
        holidayRules: [],
      });

      clearForm();
      clearForm();

      if (onCreatedCallback) onCreatedCallback(filingId);
      closeDialog();
    } catch (error) {
      setErrors({ name: "Failed to create filing. Please try again." });
    }

    setIsUploading(false);
  };

  const clearForm = () => {
    setFile(null);
    setName("");
    setOrderDate(new Date().toISOString().split("T")[0]);
    setErrors({});
    setIsOpen(false);
  };

  return (
    <CreateHolidayScheduleDialogProviderContext.Provider
      value={{ openDialog, closeDialog, isOpen }}
    >
      <Dialog open={isOpen} onClose={closeDialog}>
        <DialogTitle>Upload Holiday Schedule</DialogTitle>
        {step === "file" && (
          <DialogDescription>
            Holiday schedules can be uploaded as PDF or Word documents. Please
            ensure the file is less than 10 MB.
          </DialogDescription>
        )}
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              {step === "file" ? (
                <DragAndDropFile onFileDrop={setFile} />
              ) : (
                <>
                  <Field>
                    <Label>Name</Label>
                    <Input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Name"
                    />
                    {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
                  </Field>
                  <Field>
                    <Label>Order Date</Label>
                    <Input
                      type="date"
                      value={orderDate}
                      onChange={(e) => setOrderDate(e.target.value)}
                    />
                  </Field>
                </>
              )}
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          {step === "form" && (
            <Button
              plain
              onClick={() => {
                setFile(null);
                setStep("file");
              }}
            >
              Back
            </Button>
          )}
          {step === "file" && (
            <Button plain onClick={clearForm}>
              Cancel
            </Button>
          )}
          {step === "file" && (
            <Button onClick={() => setStep("form")}>Skip</Button>
          )}
          {step === "form" && (
            <Button
              disabled={!name.trim() || isUploading}
              onClick={handleSubmit}
            >
              {isUploading ? "Uploading..." : "Upload"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {children}
    </CreateHolidayScheduleDialogProviderContext.Provider>
  );
};

export const useCreateHolidayScheduleDialog = () => {
  const ctx = useContext(CreateHolidayScheduleDialogProviderContext);
  if (!ctx)
    throw new Error(
      "useCreateHolidayScheduleDialog must be used inside CreateHolidayScheduleDialogProvider",
    );
  return ctx;
};
